import type { ReportSummary } from '@obras/shared';
import { formatDate } from '@obras/shared';
import { ImageUp, Loader2, Trash2, X } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';

import { Button } from '../../components/ui/Button.js';
import { FormError } from '../../components/ui/Feedback.js';
import { cn } from '../../lib/cn.js';
import { ApiError } from '../../lib/api.js';
import { uploadMedia, useReports } from '../reports/api.js';

const ICON = { strokeWidth: 1.5 } as const;

/** O que o navegador aceita como capa — as mesmas imagens do resto do sistema. */
const ACEITOS = 'image/jpeg,image/png,image/webp';

interface Escolhida {
  readonly mediaId: string;
  readonly url: string;
}

/**
 * A imagem que representa a obra na listagem e no cabeçalho da ficha.
 *
 * Na criação só há o envio: a obra ainda não tem relatório, logo não tem acervo
 * de onde escolher. Na edição as duas portas ficam abertas, e a segunda é a que
 * costuma ser usada — a foto boa da fachada quase sempre já foi enviada em
 * alguma vistoria, e reenviá-la duplicaria o arquivo no acervo.
 */
export function CapaDaObra({
  obraId,
  atual,
  onChange,
}: {
  /** Ausente na criação: sem obra, não há acervo a consultar. */
  obraId?: string | undefined;
  atual: Escolhida | null;
  onChange: (escolhida: Escolhida | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [acervoAberto, setAcervoAberto] = useState(false);

  /*
   * O acervo só é buscado quando alguém pede para ver, e nunca na criação.
   * São todas as fotos de todos os relatórios da obra; carregá-las junto com o
   * formulário custaria uma requisição a quem só quer corrigir o nº do contrato.
   */
  const acervoHabilitado = Boolean(obraId) && acervoAberto;
  const relatorios = useReports({ obraId: obraId ?? '', perPage: 100 }, acervoHabilitado);

  const fotos = useMemo(() => {
    if (!acervoHabilitado) return [];

    return (relatorios.data?.items ?? []).flatMap((relatorio: ReportSummary) =>
      relatorio.media
        .filter((item) => item.kind === 'imagem')
        .map((item) => ({
          mediaId: item.id,
          url: item.url,
          origem: relatorio.title,
          quando: item.takenAt ?? relatorio.visitDate,
        })),
    );
  }, [acervoHabilitado, relatorios.data]);

  async function enviar(arquivo: File | undefined): Promise<void> {
    if (!arquivo) return;

    setErro(null);
    setEnviando(true);

    try {
      const enviada = await uploadMedia(arquivo, 'obras');
      onChange({ mediaId: enviada.id, url: URL.createObjectURL(arquivo) });
    } catch (falha) {
      setErro(falha instanceof ApiError ? falha.message : 'Não foi possível enviar a imagem.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div>
      <span className="mb-1.5 block text-[13px] font-medium">Imagem de capa</span>

      <div className="flex items-start gap-4">
        <div className="relative size-28 shrink-0 overflow-hidden rounded-md bg-muted">
          {atual ? (
            <>
              <img src={atual.url} alt="" className="size-full object-cover" />
              <button
                type="button"
                onClick={() => onChange(null)}
                aria-label="Remover a imagem de capa"
                title="Remover a imagem de capa"
                className="absolute top-1 right-1 grid size-6 place-items-center rounded-sm bg-black/55 text-white transition-colors hover:bg-black/75"
              >
                <X {...ICON} size={13} aria-hidden />
              </button>
            </>
          ) : (
            <span className="grid size-full place-items-center text-muted-foreground">
              <ImageUp {...ICON} size={22} aria-hidden />
            </span>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-[13px] text-muted-foreground">
            Exibida na listagem de obras e no cabeçalho da ficha. Sem imagem definida, o sistema
            utiliza a primeira fotografia do relatório mais recente.
          </p>

          <div className="mt-3 flex flex-wrap gap-2">
            <input
              ref={inputRef}
              type="file"
              accept={ACEITOS}
              className="sr-only"
              onChange={(event) => {
                void enviar(event.target.files?.[0]);
                event.target.value = '';
              }}
            />

            <Button
              size="sm"
              disabled={enviando}
              icon={
                enviando ? (
                  <Loader2 {...ICON} size={14} className="animate-spin" />
                ) : (
                  <ImageUp {...ICON} size={14} />
                )
              }
              onClick={() => inputRef.current?.click()}
            >
              {enviando ? 'Enviando…' : 'Enviar imagem'}
            </Button>

            {obraId ? (
              <Button size="sm" variant="ghost" onClick={() => setAcervoAberto((v) => !v)}>
                {acervoAberto ? 'Ocultar acervo' : 'Escolher do acervo'}
              </Button>
            ) : null}

            {atual ? (
              <Button
                size="sm"
                variant="ghost"
                icon={<Trash2 {...ICON} size={14} />}
                onClick={() => onChange(null)}
              >
                Remover
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      {erro ? (
        <div className="mt-3">
          <FormError>{erro}</FormError>
        </div>
      ) : null}

      {acervoHabilitado ? (
        <div className="mt-4 rounded-md bg-muted p-3">
          {relatorios.isPending ? (
            <p className="py-6 text-center text-[13px] text-muted-foreground">
              Carregando as fotografias da obra…
            </p>
          ) : fotos.length === 0 ? (
            <p className="py-6 text-center text-[13px] text-muted-foreground">
              Esta obra ainda não possui fotografias em relatórios.
            </p>
          ) : (
            <ul className="grid max-h-56 grid-cols-4 gap-1.5 overflow-y-auto sm:grid-cols-6">
              {fotos.map((foto) => (
                <li key={foto.mediaId}>
                  <button
                    type="button"
                    onClick={() => onChange({ mediaId: foto.mediaId, url: foto.url })}
                    title={`${foto.origem} · ${formatDate(foto.quando)}`}
                    aria-label={`Definir como capa a fotografia de ${formatDate(foto.quando)}`}
                    aria-pressed={atual?.mediaId === foto.mediaId}
                    className={cn(
                      'block aspect-square w-full overflow-hidden rounded-sm bg-card transition-opacity',
                      atual?.mediaId === foto.mediaId
                        ? 'ring-2 ring-primary'
                        : 'opacity-80 hover:opacity-100',
                    )}
                  >
                    <img src={foto.url} alt="" loading="lazy" className="size-full object-cover" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
